import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  CLAUDE_MODELS,
  SERVER_DEFAULT_MODEL,
  TIER_LABEL,
  type ClaudeModelOption,
} from "../../lib/claudeModels";

/**
 * Combobox for a Claude model id: free text plus a prefilled list of known
 * models with a one-line hint each, so switching a recognizer between
 * Haiku / Sonnet / Opus is a click instead of remembering an id.
 *
 * An empty value means "server default"; the placeholder says which model
 * that is.
 */
export function ModelPicker({
  value,
  onChange,
  disabled,
  id,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  id?: string;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listId = useId();

  const query = value.trim().toLowerCase();
  const options = useMemo(() => {
    // While typing, narrow the list; an exact match keeps every option so
    // the user can still browse away from the current choice.
    const exact = CLAUDE_MODELS.some((m) => m.id === query);
    if (!query || exact) return CLAUDE_MODELS;
    return CLAUDE_MODELS.filter(
      (m) => m.id.includes(query) || m.label.toLowerCase().includes(query),
    );
  }, [query]);

  const known = CLAUDE_MODELS.find((m) => m.id === value.trim());
  const custom = value.trim() !== "" && !known;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (m: ClaudeModelOption | null) => {
    onChange(m ? m.id : "");
    setOpen(false);
    setActive(-1);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && open && active >= 0 && options[active]) {
      e.preventDefault();
      pick(options[active]);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div className="mp" ref={wrapRef}>
      <div className={`mp-field${open ? " mp-field--open" : ""}`}>
        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          className="mp-input"
          value={value}
          disabled={disabled}
          placeholder={`Server default (${SERVER_DEFAULT_MODEL})`}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {known && <span className={`mp-tier mp-tier--${known.tier}`}>{TIER_LABEL[known.tier]}</span>}
        {custom && <span className="mp-tier mp-tier--custom">Custom id</span>}
        <button
          type="button"
          className="mp-toggle"
          aria-label={open ? "Close model list" : "Open model list"}
          disabled={disabled}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setOpen((o) => !o);
            inputRef.current?.focus();
          }}
        >
          <ChevronDown size={16} />
        </button>
      </div>

      {open && !disabled && (
        <ul className="mp-list" id={listId} role="listbox">
          <li
            role="option"
            aria-selected={value.trim() === ""}
            className={`mp-option${value.trim() === "" ? " mp-option--selected" : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pick(null)}
          >
            <span className="mp-option-main">
              <span className="mp-option-id">Server default</span>
              <span className="mp-option-hint">Uses {SERVER_DEFAULT_MODEL} from the voicebot settings.</span>
            </span>
            {value.trim() === "" && <Check size={14} className="mp-check" />}
          </li>
          {options.map((m, i) => {
            const selected = m.id === value.trim();
            return (
              <li
                key={m.id}
                role="option"
                aria-selected={selected}
                className={`mp-option${selected ? " mp-option--selected" : ""}${i === active ? " mp-option--active" : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => pick(m)}
              >
                <span className="mp-option-main">
                  <span className="mp-option-id">
                    {m.id}
                    <span className="mp-option-label">{m.label}</span>
                  </span>
                  <span className="mp-option-hint">{m.hint}</span>
                </span>
                <span className={`mp-tier mp-tier--${m.tier}`}>{TIER_LABEL[m.tier]}</span>
                {selected && <Check size={14} className="mp-check" />}
              </li>
            );
          })}
          {options.length === 0 && (
            <li className="mp-empty">No known model matches. The id is used as typed.</li>
          )}
        </ul>
      )}
    </div>
  );
}
